import {awaitRedraw, create} from "@dhx/ts-common/dom";
import { EventSystem, IEventSystem } from "@dhx/ts-common/events";
import { eventHandler, locate, locateNode } from "@dhx/ts-common/html";
import { View } from "@dhx/ts-common/view";
import { DataCollection, DataEvents, dhxWarning, DragEvents, dragManager, IDataCollection, IDataEventsHandlersMap, IDragEventsHandlersMap } from "@dhx/ts-data";
import { Exporter } from "./Exporter";
import { countColumns, getUnique, normalizeColumns } from "./helpers/data";
import { getStrWidth, isRowEmpty, removeHTMLTags } from "./helpers/main";
import { Selection } from "./Selection";
import { initHotkeys } from "./Hotkey";
import { Dirs, EditorType, GridEvents, ICellRect, ICol, IContentList, ICoords, IEventHandlersMap, IGrid, IGridConfig, IRow, IScrollState, ISelection, ISpan } from "./types";
import { render } from "./ui/render";

import { extend, findIndex } from "@dhx/ts-common/core";
import { content } from "./ui/content";
import { startResize } from "./columnsResizer";

export class Grid extends View implements IGrid {
	public data: IDataCollection;
	public config: IGridConfig;
	public events: IEventSystem<DataEvents | GridEvents | DragEvents, IEventHandlersMap & IDataEventsHandlersMap & IDragEventsHandlersMap>;
	public export: Exporter;
	public content: IContentList;
	public selection: ISelection;

	protected _scroll: IScrollState;
	private _sortDir: Dirs;
	private _sortBy: string;
	private _currentData: any[];
	constructor(container: HTMLElement | string, config?: IGridConfig) {
		super(container, config);
		this.config = extend({
			rowHeight: 40,
			headerRowHeight: 40,
			footerRowHeight: 40,
			headerSort: true,
			resizable: false,
			keyNavigation: true,

			columns: [],
			data: []
		} as any as IGridConfig, config);

		this.content = content;
		this._scroll = {
			top: 0,
			left: 0
		};
		const htmlEvents = {
			onclick: eventHandler(ev => locate(ev),
				{
					"dhx_grid-header-cell": (_ev, item) => {
						const resizedColumn = _ev.target.getAttribute("dhx_resized");
						if (this.config.headerSort && !resizedColumn) {
							this.events.fire(GridEvents.sort, [item]);
						}
					},
					"dhx_grid-expand-cell": (_ev, item) => {
						if (_ev.target.classList.contains("dhx_grid-expand-cell-icon")) {
							this.events.fire(GridEvents.expand, [item]);
						}
					}
				}
			),
			onscroll: e => {
				this.events.fire(GridEvents.scroll, [{
					y: e.target.scrollTop,
					x: e.target.scrollLeft
				}]);
			}
		};
		if (this.config.dragMode) {
			dragManager.setItem(this._uid, this);
			if (!this.config.dragItem) {
				this.config.dragItem = "row";
			}
		}

		this._init();

		if (this.config.columns) {
			this._parseColumns();
		}
		if ((this.config.data && this.config.data instanceof Array && this.config.data.length) && this.config.columns) {
			this.data.parse(this.config.data);
		}
		if (this.config.selection) {
			this.selection = new Selection(this);
		}
		const view = create({
			render: (vm, obj) => {
				return render(vm, obj, this._currentData, htmlEvents, this.selection, this._uid);
			}
		}, this);
		this.mount(container, view);

		awaitRedraw().then(() => {
			initHotkeys(this);
		});

		if (config.autoEmptyRow && this.data.getLength() === 0) {
			this.data.add(this.config.columns.reduce((total, col) => {
				total[col.id] = "";
				return total;
			}, {}));
			this.paint();
		}
	}
	destructor() {
		this.unmount();
		if (this.config.selection) {
			this.selection.destructor();
		}
		this._destroyContent();
		this.events.events = {};
		this.events.context = null;
		this._currentData = this.data = this.config =
			this._scroll = this.content = null;
	}
	setColumns(columns: ICol[]) {
		this.config.columns = columns;
		this._parseColumns();
	}
	addRowCss(id: string, css: string) {
		const item = this.data.getItem(id);
		const styles = item.$css || "";
		if (!styles.match(new RegExp(css, "g"))) {
			item.$css = styles + ` ${css}`;
			// [todo]
			const index = findIndex(this._currentData, row => row.id === item.id);
			if (index >= 0) {
				this._currentData[index].$css = item.$css;
			}
			this.paint();
		}
	}
	removeRowCss(id: string, css: string) {
		const item = this.data.getItem(id);
		const styles = item.$css ? item.$css.replace(css, "") : "";
		item.$css = styles;
	}
	addCellCss(row: string, col: string | number, css: string) {
		const column = this._getColumn(col);
		if (column) {
			if (column.$cellCss[row]) {
				column.$cellCss[row] += column.$cellCss[row].match(new RegExp(css, "g")) ? "" : ` ${css}`;
			} else if (this.data.getItem(row)) {
				column.$cellCss[row] = `${css} `;
			}
			this.paint();
		}
	}
	removeCellCss(row: string, col: string | number, css: string) {
		const column = this._getColumn(col);
		if (column) {
			if (column.$cellCss[row]) {
				column.$cellCss[row] = column.$cellCss[row].replace(css, "");
				this.paint();
			} else if (this.data.getItem(row)) {
				column.$cellCss[row] = "";
			}
		}
	}
	showColumn(colId: string | number) {
		const column = this._getColumn(colId);
		if (column && column.hidden) {
			column.hidden = false;
			this.config.$totalWidth += column.width;
			this.paint();
		}
	}
	hideColumn(colId: string | number) {
		const column = this._getColumn(colId);
		if (column && !column.hidden) {
			column.hidden = true;
			this.config.$totalWidth -= column.width;
			this.paint();
		}
	}
	getScrollState(): ICoords {
		return {
			x: this._scroll.left,
			y: this._scroll.top
		};
	}
	scroll(x: number, y: number) {
		const gridBody = this.getRootView().refs.grid_body.el;
		gridBody.scrollLeft = typeof x === "number" ? x : gridBody.scrollLeft;
		gridBody.scrollTop = typeof y === "number" ? y : gridBody.scrollTop;
	}
	scrollTo(row: string, col: string) {
		const colInd = findIndex(this.config.columns, obj => obj.id === col);
		const fixedColsWidth = this.config.splitAt ?
			this.config.columns.slice(0, this.config.splitAt).reduce((total, c) => total += c.width, 0)
			: 0;
		const x = this.config.columns.slice(0, colInd).reduce((total, c) => total += c.width, 0) - fixedColsWidth;
		const y = this.data.getIndex(row) * this.config.rowHeight;

		const scrollState = this.getScrollState();

		const gridRight = this.config.width + scrollState.x;
		const gridBottom = this.config.height + scrollState.y - (this.config.headerRowHeight * this.config.$headerLevel);

		const cellTop = y - scrollState.y - this.config.rowHeight;
		const cellLeft = x - scrollState.x - this.config.columns[colInd].width;

		const cellBottom = y + (this.config.rowHeight * 2) + 18 - gridBottom;
		const cellRight = x + (this.config.columns[colInd].width * 2) + 18 - gridRight;

		const scrollTop = (cellTop > 0 && cellBottom < 0) ? 0 : cellTop < 0 ? cellTop : cellBottom;
		const scrollLeft = (cellLeft > 0 && cellRight < 0) ? 0 : cellLeft < 0 ? cellLeft : cellRight;

		this.scroll(
			scrollLeft + scrollState.x,
			scrollTop + scrollState.y
		);
	}
	adjustColumnWidth(id: string | number) {
		const index = findIndex(this.config.columns, c => c.id === id);

		const col = this.config.columns[index];
		this.data.map((row) => {
			if (typeof row[col.id] === "string" || typeof row[col.id] === "number") {
				col.$maxWidth = col.$maxWidth || col.width;
				col.$maxWidth = Math.max(getStrWidth(removeHTMLTags(row[col.id])) + 20, col.$maxWidth);
			}
		});

		this.config.$totalWidth = this.config.columns.reduce((t, column) => {
			column.width = column.$maxWidth || column.width;
			return t += column.hidden ? 0 : column.width;
		}, 0);
		this.paint();
	}

	getCellRect(row: string | number, col: string | number): ICellRect {
		const colInd = findIndex(this.config.columns, obj => obj.id === col);
		const rowInd = this._getRowIndex(row);

		const x = this.config.columns.slice(0, colInd).reduce((total, c) => total += c.width, 0);
		const y = rowInd * this.config.rowHeight;
		return { x, y, height: this.config.rowHeight, width: this.config.columns[colInd].width };
	}
	getColumn(colId: string): ICol {
		const ind = findIndex(this.config.columns, col => col.id === colId);
		if (ind >= 0) {
			return this.config.columns[ind];
		}
	}
	addSpan(spanObj: ISpan) {
		this.config.spans = this.config.spans || [];
		const index = findIndex(this.config.spans, span =>
			`${span.row}` === `${spanObj.row}` && `${span.column}` === `${spanObj.column}`
		);
		if (index >= 0) {
			this.config.spans[index] = spanObj;
			return;
		}
		this.config.spans.push(spanObj);
	}
	getSpan(row: string | number, col: string | number): ISpan {
		if (this.config.spans) {
			const index = findIndex(this.config.spans, span =>
				`${span.row}` === `${row}` && `${span.column}` === `${col}`
			);
			return this.config.spans[index];
		}
	}
	removeSpan(row: string | number, col: string | number) {
		if (this.config.spans) {
			const index = findIndex(this.config.spans, span =>
				`${span.row}` === `${row}` && `${span.column}` === `${col}`
			);
			this.config.spans.splice(index, 1);
		}
	}
	edit(rowId: string | number, colId: string | number, editorType: EditorType = EditorType.input) {
		const row = this.data.getItem(rowId as any);
		const col = this.getColumn(colId as string);
		if (!row || row[colId] === undefined) {
			dhxWarning("item not found");
			return;
		}
		if (!this.events.fire(GridEvents.beforeEditStart, [row, col, editorType])) {
			return;
		}
		if (col.type === "date") {
			editorType = EditorType.datepicker;
		}
		if (col.type === "boolean") {
			editorType = EditorType.checkbox;
		}
		if (this.config.$editable && this.config.$editable.row === rowId && this.config.$editable.col === colId && this.config.$editable.editorType === editorType) {
			return;
		}
		this.config.$editable = {
			row: rowId,
			col: colId,
			editorType
		};
		if (this.selection) {
			this.selection.setCell(rowId.toString(), colId.toString());
		}
		this.paint();
		this.events.fire(GridEvents.afterEditStart, [row, col, editorType]);
	}
	editEnd(withoutSave?: boolean) {
		if (this.config.$editable && this.config.$editable.editor) {
			this.config.$editable.editor.endEdit(withoutSave);
		}
	}
	protected _parseColumns() {
		const columns = this.config.columns;
		normalizeColumns(columns);
		countColumns(this.config, columns);
	}
	protected _parseData() {
		const firstItem = this.data.getId(0);
		if (firstItem) {
			if (this.config.columns.length) {
				this._checkColumns();
			}
			this._currentData = this.data.serialize() || [];
		}
		this._checkFilters();
		this._checkMarks();
		this._render();
	}
	protected _checkColumns() {
		this._detectColsTypes();
	}
	protected _createCollection(prep: (data: any[]) => any[]) {
		this.data = new DataCollection({
			prep
		}, this.events);
	}
	protected _getRowIndex(rowId: any): number {
		return this.data.getIndex(rowId as any);
	}
	protected _setEventHandlers() {
		const updater = (updateObj: any) => (id: string, ids?: any) => {
			if (ids && ids instanceof Array) {
				ids.map(selectedId => this.data.exists(selectedId) && this.data.update(selectedId, updateObj));
				return;
			}
			if (this.data.exists(id)) {
				this.data.update(id, updateObj);
			}
		};

		this.data.events.on(DataEvents.load, () => {
			this._parseData();
			// [todo] need to check autoWidth after data load
			this.data.events.fire(DataEvents.change);
		});
		this.data.events.on(DataEvents.change, (_id, status, obj) => {
			// [dirty]
			if (status === "remove" && obj.$empty) {
				return;
			}
			this._currentData = this.data.map(el => el) || [];
			this._detectColsTypes();
			this._checkMarks();
			// set auto width to all columns
			if (this.config.columnsAutoWidth) {
				if (typeof this.config.columnsAutoWidth === "number") {
					this._setAutoWidth(this.config.columnsAutoWidth);
				} else {
					this._setAutoWidth();
				}
			}
			if (this.config.autoEmptyRow) {
				const emptyRow = this.data.find({ by: "$empty", match: true });
				if (emptyRow) {
					this.data.move(emptyRow.id, this.data.getLength() - 1);
				} else {
					this._addEmptyRow();
				}
			}
			this._render();
		});
		this.data.events.on(DataEvents.removeAll, () => {
			this.config.columns.map(col => {
				col.header.map(cell => {
					if (cell.content && (cell.content === "selectFilter" || cell.content === "comboFilter")) {
						col.$uniqueData = [];
					}
				});
			});
		});

		this.events.on(DragEvents.canDrop, updater({ $drophere: true }));
		this.events.on(DragEvents.cancelDrop, updater({ $drophere: undefined }));
		this.events.on(DragEvents.dragStart, updater({ $dragtarget: true }));
		this.events.on(DragEvents.dragEnd, updater({ $dragtarget: undefined }));

		this.events.on(GridEvents.sort, id => {
			if (id) {
				this._sort(id);
			}
		});
		this.events.on(GridEvents.cellMouseDown, (row: IRow, col: ICol, e: MouseEvent) => {
			if (this.config.dragMode && this.config.dragItem === "row" && !this.config.$editable) {
				const item = locateNode(e, "dhx_id") as HTMLElement;
				const itemId = item && item.getAttribute("dhx_id");
				return dragManager.onMouseDown(e, [itemId], [item]);
			}
		});

		// TODO: remove sute_7.0
		this.events.on(GridEvents.headerInput, (val: any, colId: string, filter: string) => {
			// [dirty]
			if (this.config.autoEmptyRow) {
				const emptyRow = this.data.find({ by: "$empty", match: true });
				if (emptyRow) {
					this.data.remove(emptyRow.id);
				}
			}
			this.data.filter({
				by: colId,
				match: val,
				compare: this.content[filter].match
			}, {
					multiple: true
				});
		});

		this.events.on(GridEvents.filterChange, (val: any, colId: string, filter: string) => {
			if (this.config.autoEmptyRow) {
				const emptyRow = this.data.find({ by: "$empty", match: true });
				if (emptyRow) {
					this.data.remove(emptyRow.id);
				}
			}
			this.data.filter({
				by: colId,
				match: val,
				compare: this.content[filter].match
			}, { multiple: true });
		});

		this.events.on(GridEvents.scroll, scrollState => {
			this._scroll = { top: scrollState.y, left: scrollState.x };
			this.paint();
		});

		this.events.on(GridEvents.cellDblClick, (row, col) => {
			if (col.editing !== false && this.config.editing || col.editing) {
				this.edit(row.id, col.id, col.editorType);
			}
		});

		this.events.on(GridEvents.afterEditEnd, (value, eRow, eCol) => {
			let row;
			let col;
			if (!this.config.$editable) {
				row = eRow.id;
				col = eCol.id;
			} else {
				row = this.config.$editable.row;
				col = this.config.$editable.col;
			}
			const item = this.data.getItem(row);
			delete item.$empty;
			if (value !== undefined) {
				this.data.update(row, { ...item, [col]: value });
			}
			this.config.$editable = null;
			this._checkFilters();
			this.paint();
		});

		this.events.on(GridEvents.headerCellMouseDown, (col: ICol, e) => {
			const resizedColumn = e.target.getAttribute("dhx_resized");
			if (resizedColumn && this.events.fire(GridEvents.beforeResizeStart, [col, e])) {
				startResize(this, resizedColumn.toString(), e, () => {
					this.paint();
					this.config.$resizing = null;
					this.events.fire(GridEvents.afterResizeEnd, [col, e]);
				});
			} else {
				return;
			}
		});
	}
	protected _addEmptyRow() {
		const id = this.data.getId(this.data.getLength() - 1);
		const lastRow = this.data.getItem(id);

		const isEmpty = isRowEmpty(lastRow);

		if (!isEmpty) {
			this.data.add(this.config.columns.reduce((total, col) => {
				total[col.id] = "";
				return total;
			}, { $empty: true }));
		}
	}
	protected _sort(by: string, dir?: Dirs) {
		if (!dir) {
			if (this._sortBy === by) {
				this._sortDir = this._sortDir === "asc" ? "desc" : "asc";
			} else {
				this._sortDir = "desc";
			}
		} else {
			this._sortDir = dir;
		}
		this._sortBy = by;
		this.data.sort({
			by,
			dir: this._sortDir,
			as: (el) => {
				if (el && this.getColumn(by).type === "date") {
					return `${new Date(el).getTime()}`;
				}
				return el ? `${el}` : "";
			}
		});
	}
	private _getColumn(id: string | number): ICol {
		for (const col of this.config.columns) {
			if (col.id === id) {
				return col;
			}
		}
	}
	private _init() {
		this.events = new EventSystem(this);
		this._attachDataCollection();
		this.export = new Exporter(this);
		this._setEventHandlers();
	}
	private _attachDataCollection() {
		const prep = (data) => {
			if (data.spans) {
				this.config.spans = data.spans;
				data = data.data;
			}
			return data;
		};
		if (this.config.data instanceof DataCollection) {
			this.data = this.config.data;
			this.config.data = [];
			this._parseData();
			return;
		}
		this._createCollection(prep);
	}
	private _setMarks(col: ICol, func: any) {
		const colCells = this.data.map(row => ({ id: row.id, data: row[col.id], row }));
		const colCellsData = this.data.map(row => row[col.id]);
		for (const cell of colCells) {
			const css = func(cell.data, colCellsData, cell.row, col);
			if (css) {
				col.$cellCss = col.$cellCss || {};
				const cellCss = (col.$cellCss[cell.id] || "").split(" ");
				css.split(" ").map(item => {
					if (cellCss.indexOf(item) === -1) {
						cellCss.push(item);
					}
				});
				col.$cellCss[cell.id] = cellCss.join(" ");
			}
		}
	}
	private _checkMarks() {
		this.config.columns.map(col => {
			const mark = col.mark;
			if (mark) {
				if (typeof mark === "function") {
					this._setMarks(col, mark);
				} else {
					this._setMarks(col, (el, c) => {
						const data = c.filter(item => item !== null && item !== undefined && item !== "");
						const min = Math.min(...data);
						const max = Math.max(...data);
						if (mark.max && max === parseFloat(el)) {
							return mark.max;
						}
						if (mark.min && min === parseFloat(el)) {
							return mark.min;
						}
						return false;
					});
				}
			}
		});
	}
	// [todo] use adjustColumnWidth
	private _setAutoWidth(colsCount?: number) {
		this.data.map(row => {
			this.config.columns.map((col, i) => {
				if (colsCount && colsCount <= i) {
					return col;
				}

				if (typeof row[col.id] === "string" || typeof row[col.id] === "number") {
					col.$maxWidth = col.$maxWidth || col.width;

					let paddings = 20;

					// [todo] move to treegrid
					if (this.config.type === "tree" && i === 0) {
						paddings = (this.data as any).getMaxLevel() * 24 + 20;
					}

					col.$maxWidth = Math.max(getStrWidth(removeHTMLTags(row[col.id])) + paddings, col.$maxWidth);
				}
			});
		});
		this.config.$totalWidth = this.config.columns.reduce((t, col) => {
			col.width = col.$maxWidth || col.width;
			if (col.maxWidth && col.width > col.maxWidth) {
				col.width = col.maxWidth;
			}
			if (col.minWidth && col.width < col.minWidth) {
				col.width = col.minWidth;
			}
			return t += col.hidden ? 0 : col.width;
		}, 0);
	}
	// [todo] make more smart type detection
	private _detectColsTypes() {
		const firstRow = this.data.getItem(this.data.getId(0));
		if (!firstRow) {
			return;
		}
		this.config.columns.map(col => {
			if (col.type) {
				return col;
			}
			const firstCell = firstRow ? firstRow[col.id] : "";
			const v = parseFloat(firstCell);
			const val = isNaN(v) ? firstCell : v;
			col.type = typeof val;
			return col;
		});
	}
	private _checkFilters() {
		const data = this._currentData;
		this.config.columns.map(col => {
			col.header.map(cell => {
				if (cell.content && (cell.content === "selectFilter" || cell.content === "comboFilter")) {
					const unique = getUnique(data, col.id as string);
					if (col.$uniqueData && col.$uniqueData.length > unique.length) {
						unique.forEach(item => {
							if (col.$uniqueData.indexOf(item) === -1) {
								col.$uniqueData.push(item);
							}
						});
					} else {
						col.$uniqueData = unique;
					}
				}
			});
		});
	}
	private _destroyContent() {
		for (const contentName in this.content) {
			if (contentName === "comboFilter") {
				this.content[contentName].destroy();
			}
		}
	}
	private _render() {
		this.paint();
	}
}